/**
 * Knowledge Base API Domain
 *
 * Type-safe API client for knowledge base operations.
 */

import type { RequestFn } from '../http';

/** Kunde, für den eine Anleitung gilt — `name` null, wenn es ihn nicht mehr gibt */
export interface KnowledgeBaseCustomer {
  id: string;
  name: string | null;
}

export interface KnowledgeBaseArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  category: string;
  tags: string[];
  /** Leer = gilt für alle Kunden */
  customerIds: string[];
  customers: KnowledgeBaseCustomer[];
  authorId: string;
  author?: { displayName: string; email?: string };
  status: 'DRAFT' | 'PENDING_APPROVAL' | 'PUBLISHED' | 'REJECTED';
  viewCount: number;
  usefulCount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  approvedBy?: { displayName: string };
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

export function createKnowledgeBaseApi(request: RequestFn) {
  return {
    /**
     * Create a new knowledge base article (DRAFT status)
     */
    async createArticle(data: {
      title: string;
      content: string;
      category: string;
      tags?: string[];
      /** Leer = alle Kunden */
      customerIds?: string[];
    }) {
      return request<KnowledgeBaseArticle>('/knowledge-base/article', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Get single article by ID or slug
     */
    async getArticle(idOrSlug: string) {
      return request<KnowledgeBaseArticle>(`/knowledge-base/article/${encodeURIComponent(idOrSlug)}`);
    },

    /**
     * List articles with filters. Ohne `mine` nur veröffentlichte; `mine: true` = eigene, jeder Status.
     */
    async listArticles(options?: {
      search?: string;
      category?: string;
      /** Was für diesen Kunden gilt: allgemeine Anleitungen plus die nur für ihn */
      customerId?: string;
      status?: string;
      limit?: number;
      offset?: number;
      mine?: boolean;
      publishedOnly?: boolean;
    }) {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.category) params.set('category', options.category);
      if (options?.customerId) params.set('customerId', options.customerId);
      if (options?.status) params.set('status', options.status);
      if (options?.limit) params.set('limit', String(options.limit));
      if (options?.offset) params.set('offset', String(options.offset));
      if (options?.mine) params.set('mine', 'true');
      if (options?.publishedOnly !== undefined) params.set('publishedOnly', String(options.publishedOnly));

      return request<{
        articles: KnowledgeBaseArticle[];
        total: number;
      }>(`/knowledge-base/articles${params.toString() ? '?' + params.toString() : ''}`);
    },

    /**
     * Update article
     */
    async updateArticle(
      id: string,
      data: {
        title?: string;
        content?: string;
        category?: string;
        tags?: string[];
        /** `[]` = wieder für alle Kunden */
        customerIds?: string[];
      }
    ) {
      return request<KnowledgeBaseArticle>(`/knowledge-base/article/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    },

    /**
     * Delete article (admin: any; author: own unpublished)
     */
    async deleteArticle(id: string) {
      return request<{ success: boolean }>(`/knowledge-base/article/${id}`, {
        method: 'DELETE',
      });
    },

    /**
     * Submit article for approval
     */
    async submitForApproval(id: string) {
      return request<KnowledgeBaseArticle>(`/knowledge-base/article/${id}/submit`, {
        method: 'POST',
      });
    },

    /**
     * Approve article (admin only)
     */
    async approveArticle(id: string) {
      return request<KnowledgeBaseArticle>(`/knowledge-base/article/${id}/approve`, {
        method: 'POST',
      });
    },

    /**
     * Reject article (admin only)
     */
    async rejectArticle(id: string, data: { reason: string }) {
      return request<KnowledgeBaseArticle>(`/knowledge-base/article/${id}/reject`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Vote article as useful
     */
    async voteUseful(id: string, isUseful = true) {
      return request<{ success: boolean }>(`/knowledge-base/article/${id}/useful`, {
        method: 'POST',
        body: JSON.stringify({ isUseful }),
      });
    },

    /**
     * Find articles relevant to a query (for assistant context)
     */
    async findRelevant(query: string, limit = 3) {
      const params = new URLSearchParams();
      params.set('query', query);
      params.set('limit', String(limit));

      return request<{ articles: KnowledgeBaseArticle[] }>(`/knowledge-base/relevant?${params.toString()}`);
    },
  };
}
